/**
 * Component 5: Process Observations
 * Multiple observation formats (4D, IDS, FMEA) with file attachments
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  ClipboardList as ClipboardIcon,
  Calendar as CalendarIcon,
  AlertTriangle as AlertTriangleIcon,
  FileText as FileIcon,
  Plus as PlusIcon,
  Edit as EditIcon,
  Trash2 as TrashIcon,
  Save as SaveIcon,
  X as XIcon
} from 'lucide-react';

import { useKataBoardV2Store } from '../../../stores/kataBoardV2Store';
import { useAlert } from '../../../hooks/useAlert';
import { processObservationApi, fileAttachmentApi } from '../../../services/kataBoardV2';
import type { KATABoardV2, ProcessObservation, FileAttachment, FileAttachmentCreate } from '../../../types/kataBoardV2';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';

interface Component05Props {
  kataBoard: KATABoardV2;
}

// PDF Preview Component
interface PDFPreviewProps {
  fileUrl: string;
  fileName: string;
}

const PDFPreview: React.FC<PDFPreviewProps> = ({ fileUrl, fileName }) => {
  const [previewError, setPreviewError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfBlob, setPdfBlob] = useState<string | null>(null);
  const [previewMethod, setPreviewMethod] = useState<'object' | 'google' | 'fallback'>('object');
  
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setIsLoading(true);
        setPreviewError(false);
        
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error('Failed to fetch PDF');
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfBlob(blobUrl);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setPreviewError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadPDF();
    
    return () => {
      if (pdfBlob) {
        URL.revokeObjectURL(pdfBlob);
      }
    };
  }, [fileUrl]);

  const renderPDFPreview = () => {
    switch (previewMethod) {
      case 'object':
        return (
          <object
            data={pdfBlob || undefined}
            type="application/pdf"
            className="w-full h-full"
          >
            <p>PDF preview not available. <a href={fileUrl} target="_blank" rel="noopener noreferrer">Download PDF</a></p>
          </object>
        );
      case 'google':
        return (
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
            className="w-full h-full"
            title={fileName}
          />
        );
      case 'fallback':
        return (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <div className="text-center">
              <FileIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">PDF Preview Not Available</h3>
              <p className="text-gray-600 mb-4">Unable to preview this PDF file</p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Download PDF
              </a>
            </div>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      {renderPDFPreview()}
      
      {/* Preview method selector */}
      <div className="absolute top-4 left-4 flex space-x-1 bg-white rounded-md shadow-sm border z-10">
        <button
          onClick={() => setPreviewMethod('object')}
          className={`px-2 py-1 text-xs rounded ${previewMethod === 'object' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          Object
        </button>
        <button
          onClick={() => setPreviewMethod('google')}
          className={`px-2 py-1 text-xs rounded ${previewMethod === 'google' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          Google
        </button>
        <button
          onClick={() => setPreviewMethod('fallback')}
          className={`px-2 py-1 text-xs rounded ${previewMethod === 'fallback' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          Download
        </button>
      </div>
    </div>
  );
};

// Resizable Preview Modal Component
interface ResizablePreviewModalProps {
  file: FileAttachment;
  onClose: () => void;
}

const ResizablePreviewModal: React.FC<ResizablePreviewModalProps> = ({ file, onClose }) => {
  const [modalSize, setModalSize] = useState({ width: 900, height: 700 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  // Center modal on mount
  useEffect(() => {
    const centerModal = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      setModalPosition({
        x: (windowWidth - modalSize.width) / 2,
        y: (windowHeight - modalSize.height) / 2
      });
    };
    centerModal();
  }, [modalSize.width, modalSize.height]);

  // Handle drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setModalPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        setModalSize({
          width: Math.max(400, resizeStart.width + deltaX),
          height: Math.max(300, resizeStart.height + deltaY)
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.resize-handle') || (e.target as HTMLElement).closest('button')) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    });
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: modalSize.width,
      height: modalSize.height
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl border relative"
        style={{
          width: modalSize.width,
          height: modalSize.height,
          left: modalPosition.x,
          top: modalPosition.y,
          position: 'fixed'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg cursor-move">
          <div className="flex items-center space-x-3">
            <FileIcon className="h-5 w-5 text-gray-600" />
            <div>
              <h3 className="font-medium text-gray-900">{file.title || file.original_filename}</h3>
              <p className="text-sm text-gray-500">{file.original_filename}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 p-0" style={{ height: modalSize.height - 60 }}>
          {file.is_image ? (
            <img
              src={fileAttachmentApi.getDownloadUrl(file)}
              alt={file.original_filename}
              className="w-full h-full object-contain"
            />
          ) : file.is_pdf ? (
            <div className="w-full h-full">
              <PDFPreview 
                fileUrl={fileAttachmentApi.getDownloadUrl(file)}
                fileName={file.original_filename}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Not Available</h3>
                <p className="text-gray-600 mb-4">This file type cannot be previewed</p>
                <a
                  href={fileAttachmentApi.getDownloadUrl(file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Download File
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Resize Handle */}
        <div
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 bg-gray-300 cursor-se-resize"
          onMouseDown={handleResizeMouseDown}
          style={{
            background: 'linear-gradient(-45deg, transparent 30%, #9CA3AF 30%, #9CA3AF 70%, transparent 70%)'
          }}
        />
      </div>
    </div>
  );
};

type ObservationType = 'fourD' | 'ids' | 'fmea' | 'files';

const Component05_ProcessObservations: React.FC<Component05Props> = ({ kataBoard }) => {
  const { setEditing, setUnsavedChanges } = useKataBoardV2Store();
  const { confirmDelete, AlertComponent } = useAlert();
  const [searchParams] = useSearchParams();
  
  // Detect if we're in display mode based on URL parameters
  const isDisplayMode = searchParams.get('mode') === 'display';
  const canEdit = useKataBoardCanEdit();
  

  // State management
  const [currentTab, setCurrentTab] = useState<ObservationType>('fourD');
  const [observations, setObservations] = useState<ProcessObservation[]>([]);
  const [selectedObservation, setSelectedObservation] = useState<ProcessObservation | null>(null);
  const [isEditing, setIsEditingLocal] = useState(false);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [newObservationTitle, setNewObservationTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Local editing state - only updated locally until save
  const [localObservationData, setLocalObservationData] = useState<any>(null);
  
  // Track previous observation to detect changes
  const previousObservationId = useRef<number | null>(null);
  
  // File management state
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [previewFile, setPreviewFile] = useState<FileAttachment | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);
  

  useEffect(() => {
    setEditing(isEditing);
  }, [isEditing, setEditing]);

  // Exit edit mode when selected observation changes
  useEffect(() => {
    const currentObservationId = selectedObservation?.id || null;
    
    // If the observation ID changed and we were in edit mode, exit edit mode
    if (previousObservationId.current !== null && 
        previousObservationId.current !== currentObservationId && 
        isEditing) {
      console.log('Observation changed from', previousObservationId.current, 'to', currentObservationId, '- exiting edit mode');
      setIsEditingLocal(false);
      setLocalObservationData(null);
      setUnsavedChanges(false);
    }
    
    // Update the previous observation ID
    previousObservationId.current = currentObservationId;
  }, [selectedObservation?.id, isEditing]);

  // Ensure existing observations start in read-only mode
  useEffect(() => {
    if (selectedObservation) {
      setIsEditingLocal(false);
      setLocalObservationData(null);
      setUnsavedChanges(false);
    }
  }, [selectedObservation]);

  // Load observations from API
  useEffect(() => {
    const loadObservations = async () => {
      if (!kataBoard.id) return;
      
      try {
        setLoading(true);
        setError(null);
        const data = await processObservationApi.getAll(kataBoard.id);
        setObservations(data);
      } catch (error: any) {
        console.error('Failed to load observations:', error);
        setObservations([]); // Set empty array on error
        
        // Set user-friendly error message
        if (error?.response?.status === 401) {
          setError('You do not have permission to access this KATA board');
        } else if (error?.response?.status === 404) {
          setError('KATA board not found');
        } else {
          setError('Failed to load process observations. Please try refreshing the page.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadObservations();
  }, [kataBoard.id]);

  // Load file attachments
  useEffect(() => {
    const loadAttachments = async () => {
      if (!kataBoard.id) return;
      
      try {
        const files = await fileAttachmentApi.getAll({ 
          kata_board: kataBoard.id, 
          attachment_type: 'observation_file' 
        });
        // Ensure files is always an array
        setAttachments(Array.isArray(files) ? files : []);
      } catch (error) {
        console.error('Failed to load attachments:', error);
        // Set to empty array on error
        setAttachments([]);
      }
    };

    loadAttachments();
  }, [kataBoard.id]);

  // In display mode, auto-select first observation of current tab
  useEffect(() => {
    if (isDisplayMode && observations.length > 0 && !selectedObservation) {
      const currentTabObservations = observations.filter(obs => obs.observation_type === currentTab);
      if (currentTabObservations.length > 0) {
        setSelectedObservation(currentTabObservations[0]);
      }
    }
  }, [isDisplayMode, observations, currentTab, selectedObservation]);

  // Handle saving changes to backend
  const handleSaveChanges = async () => {
    if (!selectedObservation || !localObservationData) return;
    
    try {
      setLoading(true);
      const updatedObservation = await processObservationApi.update(selectedObservation.id, {
        data: localObservationData
      });
      
      // Update the observations list and selected observation
      setObservations(prev => 
        prev.map(obs => obs.id === selectedObservation.id ? updatedObservation : obs)
      );
      setSelectedObservation(updatedObservation);
      setLocalObservationData(null);
      setIsEditingLocal(false);
      setUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save observation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tab configuration
  const tabs = [
    {
      id: 'fourD' as ObservationType,
      name: '4D Problem Solving',
      icon: ClipboardIcon,
      color: 'blue',
      description: 'Define, Describe, Develop, Deploy methodology'
    },
    {
      id: 'ids' as ObservationType,
      name: 'IDS Summary',
      icon: CalendarIcon,
      color: 'green',
      description: 'Intensive Days Summary observations'
    },
    {
      id: 'fmea' as ObservationType,
      name: 'FMEA Analysis',
      icon: AlertTriangleIcon,
      color: 'yellow',
      description: 'Failure Mode and Effects Analysis'
    },
    {
      id: 'files' as ObservationType,
      name: 'File Attachments',
      icon: FileIcon,
      color: 'purple',
      description: 'Documents and supporting files'
    }
  ];

  const currentTabConfig = tabs.find(tab => tab.id === currentTab);
  const currentObservations = Array.isArray(observations) 
    ? observations.filter(obs => obs.observation_type === currentTab)
    : [];

  const handleAddObservation = async () => {
    if (!newObservationTitle.trim() || currentTab === 'files') return;

    try {
      setLoading(true);
      const newObservation = await processObservationApi.create({
        kata_board: kataBoard.id,
        observation_type: currentTab as 'fourD' | 'ids' | 'fmea',
        title: newObservationTitle.trim(),
        data: getDefaultContent(currentTab)
      });

      setObservations(prev => [...prev, newObservation]);
      setSelectedObservation(newObservation);
      setNewObservationTitle('');
      setShowAddForm(false);
      setIsEditingLocal(true);
      setUnsavedChanges(true);
    } catch (error) {
      console.error('Failed to create observation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteObservation = async (observation: ProcessObservation) => {
    await confirmDelete(observation.title, async () => {
      try {
        await processObservationApi.delete(observation.id);
        setObservations(prev => prev.filter(obs => obs.id !== observation.id));
        if (selectedObservation?.id === observation.id) {
          setSelectedObservation(null);
        }
      } catch (error) {
        console.error('Failed to delete observation:', error);
      }
    });
  };

  const getDefaultContent = (type: ObservationType) => {
    switch (type) {
      case 'fourD':
        return {
          define: { problem_statement: '', scope: '', timeline: '' },
          describe: { current_state: '', data_collection: '', process_flow: '' },
          develop: { root_cause_analysis: '', solutions: [] },
          deploy: { implementation_plan: '', verification_method: '', results: '' }
        };
      case 'ids':
        return {
          date: new Date().toISOString().split('T')[0],
          shift: 'day',
          observations: [],
          summary: '',
          key_insights: []
        };
      case 'fmea':
        return {
          process_steps: []
        };
      default:
        return {};
    }
  };

  const renderObservationList = () => (
    <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col">
      {/* Tab Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2 mb-2">
          {currentTabConfig && (
            <currentTabConfig.icon className={`h-5 w-5 text-${currentTabConfig.color}-600`} />
          )}
          <h3 className="font-semibold text-gray-900">{currentTabConfig?.name}</h3>
        </div>
        <p className="text-xs text-gray-500">{currentTabConfig?.description}</p>
      </div>

      {/* Add New Button - Only show when the user can edit */}
      {!isDisplayMode && canEdit && (
        <div className="p-4 border-b border-gray-200">
          {showAddForm ? (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Enter observation title..."
                value={newObservationTitle}
                onChange={(e) => setNewObservationTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddObservation();
                  if (e.key === 'Escape') {
                    setShowAddForm(false);
                    setNewObservationTitle('');
                  }
                }}
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleAddObservation}
                  className="flex-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewObservationTitle('');
                  }}
                  className="flex-1 px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className={`w-full flex items-center justify-center space-x-2 px-3 py-2 border-2 border-dashed border-${currentTabConfig?.color}-300 text-${currentTabConfig?.color}-600 rounded-md hover:border-${currentTabConfig?.color}-400 hover:text-${currentTabConfig?.color}-700 focus:outline-none focus:ring-2 focus:ring-${currentTabConfig?.color}-500`}
            >
              <PlusIcon className="h-4 w-4" />
              <span className="text-sm font-medium">New {currentTabConfig?.name}</span>
            </button>
          )}
        </div>
      )}

      {/* Observations List */}
      <div className="flex-1 overflow-y-auto">
        {currentObservations.length === 0 ? (
          <div className="p-4 text-center">
            <p className="text-sm text-gray-500">No observations yet</p>
            {!isDisplayMode && canEdit && (
              <p className="text-xs text-gray-400 mt-1">Click "New {currentTabConfig?.name}" to get started</p>
            )}
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {currentObservations.map((observation) => (
              <div
                key={observation.id}
                onClick={() => setSelectedObservation(observation)}
                className={`p-3 rounded-md cursor-pointer transition-colors ${
                  selectedObservation?.id === observation.id
                    ? `bg-${currentTabConfig?.color}-100 border border-${currentTabConfig?.color}-300`
                    : 'bg-white hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {observation.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(observation.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  {/* Edit/Delete buttons */}
                  {!isDisplayMode && canEdit && (
                    <div className="flex space-x-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedObservation(observation);
                          setIsEditingLocal(true);
                          // Initialize local data with proper structure merged with existing data
                          const defaultData = getDefaultContent(currentTab);
                          const existingData = observation?.data || {};
                          
                          let mergedData;
                          if (currentTab === 'fourD') {
                            // Special handling for 4D structure
                            mergedData = {
                              ...defaultData,
                              ...existingData,
                              // Ensure nested objects are properly merged
                              define: { ...defaultData.define, ...existingData.define },
                              describe: { ...defaultData.describe, ...existingData.describe },
                              develop: { 
                                ...defaultData.develop, 
                                ...existingData.develop,
                                // Preserve solutions array
                                solutions: existingData.develop?.solutions || defaultData.develop?.solutions || []
                              },
                              deploy: { ...defaultData.deploy, ...existingData.deploy }
                            };
                          } else if (currentTab === 'ids') {
                            // Special handling for IDS structure
                            mergedData = {
                              ...defaultData,
                              ...existingData,
                              // Preserve arrays
                              observations: existingData.observations || defaultData.observations || [],
                              key_insights: existingData.key_insights || defaultData.key_insights || []
                            };
                          } else if (currentTab === 'fmea') {
                            // Special handling for FMEA structure
                            mergedData = {
                              ...defaultData,
                              ...existingData,
                              // Preserve process steps array
                              process_steps: existingData.process_steps || defaultData.process_steps || []
                            };
                          } else {
                            // For other observation types, simple merge
                            mergedData = { ...defaultData, ...existingData };
                          }
                          
                          setLocalObservationData(mergedData);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none"
                      >
                        <EditIcon className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteObservation(observation);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 focus:outline-none"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderMainContent = () => {
    if (currentTab === 'files') {
      return (
        <div className="flex-1 p-6 overflow-y-auto">
          {renderFilesContent()}
        </div>
      );
    }

    if (!selectedObservation) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            {currentTabConfig && (
              <currentTabConfig.icon className={`mx-auto h-12 w-12 text-${currentTabConfig.color}-400 mb-4`} />
            )}
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Select or create a {currentTabConfig?.name.toLowerCase()}
            </h3>
            <p className="text-gray-500">
              Choose an observation from the sidebar or create a new one to get started
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Observation Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {currentTabConfig && (
                <currentTabConfig.icon className={`h-6 w-6 text-${currentTabConfig.color}-600`} />
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedObservation.title}</h2>
                <p className="text-sm text-gray-500">
                  Last updated: {new Date(selectedObservation.updated_at).toLocaleString()}
                </p>
                {!isEditing && !isDisplayMode && canEdit && (
                  <p className="text-xs text-blue-600 mt-1">
                    Click "Edit" button to modify the content
                  </p>
                )}
              </div>
            </div>
            
            {!isEditing && !isDisplayMode && canEdit && currentTabConfig && (
              <button
                onClick={() => {
                  setIsEditingLocal(true);
                  // Initialize local data with proper structure merged with existing data
                  const defaultData = getDefaultContent(currentTab);
                  const existingData = selectedObservation?.data || {};
                  
                  let mergedData;
                  if (currentTab === 'fourD') {
                    // Special handling for 4D structure
                    mergedData = {
                      ...defaultData,
                      ...existingData,
                      // Ensure nested objects are properly merged
                      define: { ...defaultData.define, ...existingData.define },
                      describe: { ...defaultData.describe, ...existingData.describe },
                      develop: { 
                        ...defaultData.develop, 
                        ...existingData.develop,
                        // Preserve solutions array
                        solutions: existingData.develop?.solutions || defaultData.develop?.solutions || []
                      },
                      deploy: { ...defaultData.deploy, ...existingData.deploy }
                    };
                  } else if (currentTab === 'ids') {
                    // Special handling for IDS structure
                    mergedData = {
                      ...defaultData,
                      ...existingData,
                      // Preserve arrays
                      observations: existingData.observations || defaultData.observations || [],
                      key_insights: existingData.key_insights || defaultData.key_insights || []
                    };
                  } else if (currentTab === 'fmea') {
                    // Special handling for FMEA structure
                    mergedData = {
                      ...defaultData,
                      ...existingData,
                      // Preserve process steps array
                      process_steps: existingData.process_steps || defaultData.process_steps || []
                    };
                  } else {
                    // For other observation types, simple merge
                    mergedData = { ...defaultData, ...existingData };
                  }
                  
                  console.log('Initializing local data:', mergedData);
                  setLocalObservationData(mergedData);
                }}
                className={`flex items-center space-x-2 px-4 py-2 bg-${currentTabConfig.color}-600 text-white rounded-md hover:bg-${currentTabConfig.color}-700 focus:outline-none focus:ring-2 focus:ring-${currentTabConfig.color}-500`}
              >
                <EditIcon className="h-4 w-4" />
                <span>Edit</span>
              </button>
            )}
          </div>

          {/* Observation Content */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {renderObservationContent()}
          </div>

          {/* Edit Actions */}
          {isEditing && (
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setIsEditingLocal(false);
                  setLocalObservationData(null);
                  setUnsavedChanges(false);
                }}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                <XIcon className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSaveChanges}
                className={`flex items-center space-x-2 px-4 py-2 ${currentTabConfig ? `bg-${currentTabConfig.color}-600 hover:bg-${currentTabConfig.color}-700 focus:ring-${currentTabConfig.color}-500` : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'} text-white rounded-md focus:outline-none focus:ring-2`}
              >
                <SaveIcon className="h-4 w-4" />
                <span>Save Changes</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFourDContent = () => {
    if (!selectedObservation) return null;

    // Use local data when editing, otherwise use saved data
    const fourDData = isEditing && localObservationData ? localObservationData : selectedObservation.data;
    
    
    // 4D Phase configuration
    const fourDPhases = [
      {
        id: 'define',
        title: 'Define',
        subtitle: 'Problem Definition & Scope',
        color: 'red',
        icon: '🎯',
        description: 'Clearly define the problem, scope, and timeline',
        fields: [
          { key: 'problem_statement', label: 'Problem Statement', type: 'textarea', placeholder: 'Describe the problem in detail...' },
          { key: 'scope', label: 'Scope & Boundaries', type: 'textarea', placeholder: 'Define what is included and excluded...' },
          { key: 'timeline', label: 'Timeline & Urgency', type: 'text', placeholder: 'When does this need to be resolved?' }
        ]
      },
      {
        id: 'describe',
        title: 'Describe',
        subtitle: 'Current State Analysis',
        color: 'blue',
        icon: '📊',
        description: 'Analyze and document the current state',
        fields: [
          { key: 'current_state', label: 'Current State Description', type: 'textarea', placeholder: 'Describe the current state in detail...' },
          { key: 'data_collection', label: 'Data Collection & Metrics', type: 'textarea', placeholder: 'What data have you collected? What metrics are relevant?' },
          { key: 'process_flow', label: 'Process Flow Analysis', type: 'textarea', placeholder: 'Describe the current process flow...' }
        ]
      },
      {
        id: 'develop',
        title: 'Develop',
        subtitle: 'Root Cause & Solutions',
        color: 'green',
        icon: '💡',
        description: 'Identify root causes and develop solutions',
        fields: [
          { key: 'root_cause_analysis', label: 'Root Cause Analysis', type: 'textarea', placeholder: 'What are the root causes? Use tools like 5-Why, Fishbone...' }
        ]
      },
      {
        id: 'deploy',
        title: 'Deploy',
        subtitle: 'Implementation & Verification',
        color: 'purple',
        icon: '🚀',
        description: 'Implement solutions and verify results',
        fields: [
          { key: 'implementation_plan', label: 'Implementation Plan', type: 'textarea', placeholder: 'How will you implement the solutions?' },
          { key: 'verification_method', label: 'Verification Method', type: 'textarea', placeholder: 'How will you verify the solution works?' },
          { key: 'results', label: 'Results & Outcomes', type: 'textarea', placeholder: 'What were the actual results?' }
        ]
      }
    ];

    const updateFourDData = (phaseId: string, fieldKey: string, value: string | any[]) => {
      if (!localObservationData) return;
      
      const updatedData = {
        ...localObservationData,
        [phaseId]: {
          ...localObservationData[phaseId],
          [fieldKey]: value
        }
      };
      
      setLocalObservationData(updatedData);
      setUnsavedChanges(true);
    };

    const addSolution = () => {
      const newSolution = {
        id: Date.now().toString(),
        solution: '',
        priority: 'medium' as const,
        owner: '',
        deadline: ''
      };
      
      updateFourDData('develop', 'solutions', [...(fourDData.develop?.solutions || []), newSolution]);
    };

    const updateSolution = (solutionId: string, field: string, value: string) => {
      const solutions = fourDData.develop?.solutions || [];
      const updatedSolutions = solutions.map((sol: any) => 
        sol.id === solutionId ? { ...sol, [field]: value } : sol
      );
      updateFourDData('develop', 'solutions', updatedSolutions);
    };

    const removeSolution = (solutionId: string) => {
      const solutions = fourDData.develop?.solutions || [];
      const updatedSolutions = solutions.filter((sol: any) => sol.id !== solutionId);
      updateFourDData('develop', 'solutions', updatedSolutions);
    };

    return (
      <div className="space-y-8">
        {/* 4D Progress Indicator */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-blue-900">4D Problem Solving Methodology</h3>
            <div className="flex items-center space-x-2">
              {fourDPhases.map((phase, index) => {
                const phaseData = fourDData[phase.id] || {};
                const isComplete = Object.values(phaseData).some((value: any) => 
                  typeof value === 'string' ? value.trim() : 
                  Array.isArray(value) ? value.length > 0 : false
                );
                
                console.log(`Phase ${index + 1} (${phase.id}):`, { phaseData, isComplete, color: phase.color });
                
                // Get explicit color classes to avoid Tailwind CSS purging issues
                const getPhaseColors = (color: string, isComplete: boolean) => {
                  const colorMap = {
                    red: {
                      complete: 'bg-red-500 text-white',
                      incomplete: 'bg-red-100 text-red-700 border-2 border-red-300',
                      connector: 'bg-red-300'
                    },
                    blue: {
                      complete: 'bg-blue-500 text-white',
                      incomplete: 'bg-blue-100 text-blue-700 border-2 border-blue-300',
                      connector: 'bg-blue-300'
                    },
                    green: {
                      complete: 'bg-green-500 text-white',
                      incomplete: 'bg-green-100 text-green-700 border-2 border-green-300',
                      connector: 'bg-green-300'
                    },
                    purple: {
                      complete: 'bg-purple-500 text-white',
                      incomplete: 'bg-purple-100 text-purple-700 border-2 border-purple-300',
                      connector: 'bg-purple-300'
                    }
                  };
                  
                  const colors = colorMap[color as keyof typeof colorMap] || colorMap.blue;
                  return {
                    circle: isComplete ? colors.complete : colors.incomplete,
                    connector: isComplete ? colors.connector : 'bg-gray-200'
                  };
                };
                
                const phaseColors = getPhaseColors(phase.color, isComplete);
                
                return (
                  <div key={phase.id} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${phaseColors.circle}`}>
                      {isComplete ? '✓' : index + 1}
                    </div>
                    {index < fourDPhases.length - 1 && (
                      <div className={`w-8 h-0.5 mx-1 ${phaseColors.connector}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-blue-700 text-sm">
            Systematic approach: Define the problem → Describe current state → Develop solutions → Deploy and verify
          </p>
        </div>

        {/* 4D Phases */}
        {fourDPhases.map((phase) => {
          const phaseData = fourDData[phase.id] || {};
          
          // Debug logging for each phase
          if (isEditing) {
            console.log(`Phase ${phase.id} data:`, phaseData);
          }
          
          return (
            <div key={phase.id} className={`bg-white rounded-lg border-l-4 border-${phase.color}-500 shadow-sm`}>
              {/* Phase Header */}
              <div className={`bg-${phase.color}-50 px-6 py-4 border-b border-${phase.color}-100`}>
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{phase.icon}</span>
                  <div>
                    <h4 className={`text-lg font-semibold text-${phase.color}-900`}>
                      {phase.title}: {phase.subtitle}
                    </h4>
                    <p className={`text-sm text-${phase.color}-700 mt-1`}>{phase.description}</p>
                  </div>
                </div>
              </div>

              {/* Phase Content */}
              <div className="p-6 space-y-4">
                {phase.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                      {!isEditing && (
                        <span className="text-xs text-gray-500 ml-2">(Read-only - click Edit to modify)</span>
                      )}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={phaseData[field.key] || ''}
                        onChange={(e) => updateFourDData(phase.id, field.key, e.target.value)}
                        placeholder={field.placeholder}
                        rows={4}
                        readOnly={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-${phase.color}-500 focus:border-transparent resize-none ${
                          !isEditing ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''
                        }`}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={phaseData[field.key] || ''}
                        onChange={(e) => updateFourDData(phase.id, field.key, e.target.value)}
                        placeholder={field.placeholder}
                        readOnly={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-${phase.color}-500 focus:border-transparent ${
                          !isEditing ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''
                        }`}
                      />
                    )}
                  </div>
                ))}

                {/* Solutions Management (Develop Phase) */}
                {phase.id === 'develop' && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Proposed Solutions
                      </label>
                      {isEditing && (
                        <button
                          onClick={addSolution}
                          className={`flex items-center space-x-2 px-3 py-1 bg-${phase.color}-600 text-white text-sm rounded hover:bg-${phase.color}-700 focus:outline-none focus:ring-2 focus:ring-${phase.color}-500`}
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span>Add Solution</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {(phaseData.solutions || []).map((solution: any, index: number) => (
                        <div key={solution.id} className={`bg-${phase.color}-25 border border-${phase.color}-200 rounded-lg p-4`}>
                          <div className="flex items-start justify-between mb-3">
                            <h5 className={`font-medium text-${phase.color}-900`}>Solution {index + 1}</h5>
                            {isEditing && (
                              <button
                                onClick={() => removeSolution(solution.id)}
                                className="text-red-600 hover:text-red-800 focus:outline-none"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Solution Description</label>
                              <textarea
                                value={solution.solution}
                                onChange={(e) => updateSolution(solution.id, 'solution', e.target.value)}
                                placeholder="Describe the solution..."
                                rows={2}
                                readOnly={!isEditing}
                                className={`w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-${phase.color}-500 resize-none ${
                                  !isEditing ? 'bg-gray-50 text-gray-600' : ''
                                }`}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                              <select
                                value={solution.priority}
                                onChange={(e) => updateSolution(solution.id, 'priority', e.target.value)}
                                disabled={!isEditing}
                                className={`w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-${phase.color}-500 ${
                                  !isEditing ? 'bg-gray-50 text-gray-600' : ''
                                }`}
                              >
                                <option value="high">High Priority</option>
                                <option value="medium">Medium Priority</option>
                                <option value="low">Low Priority</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Owner</label>
                              <input
                                type="text"
                                value={solution.owner}
                                onChange={(e) => updateSolution(solution.id, 'owner', e.target.value)}
                                placeholder="Responsible person"
                                disabled={!isEditing}
                                className={`w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-${phase.color}-500 ${
                                  !isEditing ? 'bg-gray-50 text-gray-600' : ''
                                }`}
                              />
                            </div>
                            
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Target Deadline</label>
                              <input
                                type="date"
                                value={solution.deadline}
                                onChange={(e) => updateSolution(solution.id, 'deadline', e.target.value)}
                                disabled={!isEditing}
                                className={`w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-${phase.color}-500 ${
                                  !isEditing ? 'bg-gray-50 text-gray-600' : ''
                                }`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {(!phaseData.solutions || phaseData.solutions.length === 0) && (
                        <div className="text-center py-6 text-gray-500">
                          <p className="text-sm">No solutions added yet</p>
                          {isEditing && (
                            <p className="text-xs mt-1">Click "Add Solution" to get started</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderIdsContent = () => {
    if (!selectedObservation) return null;

    // Use local data when editing, otherwise use saved data
    const idsData = isEditing && localObservationData ? localObservationData : selectedObservation.data;
    
    // Debug logging
    if (isEditing) {
      console.log('IDS edit mode - localObservationData:', localObservationData);
      console.log('idsData:', idsData);
    }

    const updateIdsData = (fieldKey: string, value: string | any[]) => {
      if (!localObservationData) return;
      
      const updatedData = {
        ...localObservationData,
        [fieldKey]: value
      };
      
      setLocalObservationData(updatedData);
      setUnsavedChanges(true);
    };

    const addObservation = () => {
      const newObservation = {
        id: Date.now().toString(),
        time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
        description: '',
        category: 'process',
        severity: 'medium'
      };
      
      const currentObservations = idsData.observations || [];
      updateIdsData('observations', [...currentObservations, newObservation]);
    };

    const updateObservation = (observationId: string, field: string, value: string) => {
      const observations = idsData.observations || [];
      const updatedObservations = observations.map((obs: any) => 
        obs.id === observationId ? { ...obs, [field]: value } : obs
      );
      updateIdsData('observations', updatedObservations);
    };

    const removeObservation = (observationId: string) => {
      const observations = idsData.observations || [];
      const updatedObservations = observations.filter((obs: any) => obs.id !== observationId);
      updateIdsData('observations', updatedObservations);
    };

    const addKeyInsight = () => {
      const newInsight = {
        id: Date.now().toString(),
        insight: '',
        priority: 'medium',
        action_required: ''
      };
      
      const currentInsights = idsData.key_insights || [];
      updateIdsData('key_insights', [...currentInsights, newInsight]);
    };

    const updateKeyInsight = (insightId: string, field: string, value: string) => {
      const insights = idsData.key_insights || [];
      const updatedInsights = insights.map((insight: any) => 
        insight.id === insightId ? { ...insight, [field]: value } : insight
      );
      updateIdsData('key_insights', updatedInsights);
    };

    const removeKeyInsight = (insightId: string) => {
      const insights = idsData.key_insights || [];
      const updatedInsights = insights.filter((insight: any) => insight.id !== insightId);
      updateIdsData('key_insights', updatedInsights);
    };

    return (
      <div className="space-y-8">
        {/* IDS Header */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center space-x-3 mb-4">
            <CalendarIcon className="h-8 w-8 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-green-900">Intensive Days Summary (IDS)</h3>
              <p className="text-green-700 text-sm">Daily observations and insights tracking</p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={idsData.date || ''}
                onChange={(e) => updateIdsData('date', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  !isEditing ? 'bg-gray-50 text-gray-600' : ''
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
              <select
                value={idsData.shift || 'day'}
                onChange={(e) => updateIdsData('shift', e.target.value)}
                disabled={!isEditing}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  !isEditing ? 'bg-gray-50 text-gray-600' : ''
                }`}
              >
                <option value="day">Day Shift</option>
                <option value="night">Night Shift</option>
                <option value="afternoon">Afternoon Shift</option>
              </select>
            </div>
          </div>
        </div>

        {/* Observations */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-medium text-gray-900">Process Observations</h4>
            {isEditing && (
              <button
                onClick={addObservation}
                className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Observation</span>
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            {(idsData.observations || []).map((observation: any, index: number) => (
              <div key={observation.id} className="bg-green-25 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                      #{index + 1}
                    </span>
                    <input
                      type="time"
                      value={observation.time || ''}
                      onChange={(e) => updateObservation(observation.id, 'time', e.target.value)}
                      disabled={!isEditing}
                      className={`px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    />
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => removeObservation(observation.id)}
                      className="text-red-600 hover:text-red-800 focus:outline-none"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={observation.category || 'process'}
                      onChange={(e) => updateObservation(observation.id, 'category', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    >
                      <option value="process">Process</option>
                      <option value="quality">Quality</option>
                      <option value="safety">Safety</option>
                      <option value="efficiency">Efficiency</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Severity</label>
                    <select
                      value={observation.severity || 'medium'}
                      onChange={(e) => updateObservation(observation.id, 'severity', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={observation.description || ''}
                    onChange={(e) => updateObservation(observation.id, 'description', e.target.value)}
                    placeholder="Describe what you observed..."
                    disabled={!isEditing}
                    rows={2}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm ${
                      !isEditing ? 'bg-gray-50 text-gray-600' : ''
                    }`}
                  />
                </div>
              </div>
            ))}
            
            {(!idsData.observations || idsData.observations.length === 0) && (
              <div className="text-center py-6 text-gray-500">
                <CalendarIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p>No observations recorded yet</p>
                {isEditing && (
                  <p className="text-sm">Click "Add Observation" to start recording</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Daily Summary</h4>
          <textarea
            value={idsData.summary || ''}
            onChange={(e) => updateIdsData('summary', e.target.value)}
            placeholder="Provide a summary of the day's observations and activities..."
            disabled={!isEditing}
            rows={4}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${
              !isEditing ? 'bg-gray-50 text-gray-600' : ''
            }`}
          />
        </div>

        {/* Key Insights */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-medium text-gray-900">Key Insights & Actions</h4>
            {isEditing && (
              <button
                onClick={addKeyInsight}
                className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Insight</span>
              </button>
            )}
          </div>
          
          <div className="space-y-4">
            {(idsData.key_insights || []).map((insight: any, index: number) => (
              <div key={insight.id} className="bg-emerald-25 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-1 rounded">
                    Insight #{index + 1}
                  </span>
                  {isEditing && (
                    <button
                      onClick={() => removeKeyInsight(insight.id)}
                      className="text-red-600 hover:text-red-800 focus:outline-none"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={insight.priority || 'medium'}
                      onChange={(e) => updateKeyInsight(insight.id, 'priority', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Insight</label>
                    <textarea
                      value={insight.insight || ''}
                      onChange={(e) => updateKeyInsight(insight.id, 'insight', e.target.value)}
                      placeholder="Describe the key insight or learning..."
                      disabled={!isEditing}
                      rows={2}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Action Required</label>
                    <textarea
                      value={insight.action_required || ''}
                      onChange={(e) => updateKeyInsight(insight.id, 'action_required', e.target.value)}
                      placeholder="What action should be taken based on this insight?"
                      disabled={!isEditing}
                      rows={2}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            {(!idsData.key_insights || idsData.key_insights.length === 0) && (
              <div className="text-center py-6 text-gray-500">
                <div className="mx-auto h-8 w-8 text-gray-400 mb-2">💡</div>
                <p>No key insights recorded yet</p>
                {isEditing && (
                  <p className="text-sm">Click "Add Insight" to capture important learnings</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFmeaContent = () => {
    if (!selectedObservation) return null;

    // Use local data when editing, otherwise use saved data
    const fmeaData = isEditing && localObservationData ? localObservationData : selectedObservation.data;
    
    // Debug logging
    if (isEditing) {
      console.log('FMEA edit mode - localObservationData:', localObservationData);
      console.log('fmeaData:', fmeaData);
    }

    const updateFmeaData = (fieldKey: string, value: any[]) => {
      if (!localObservationData) return;
      
      const updatedData = {
        ...localObservationData,
        [fieldKey]: value
      };
      
      setLocalObservationData(updatedData);
      setUnsavedChanges(true);
    };

    const addProcessStep = () => {
      const newStep = {
        id: Date.now().toString(),
        process_step: '',
        function: '',
        failure_modes: []
      };
      
      const currentSteps = fmeaData.process_steps || [];
      updateFmeaData('process_steps', [...currentSteps, newStep]);
    };

    const updateProcessStep = (stepId: string, field: string, value: string) => {
      const steps = fmeaData.process_steps || [];
      const updatedSteps = steps.map((step: any) => 
        step.id === stepId ? { ...step, [field]: value } : step
      );
      updateFmeaData('process_steps', updatedSteps);
    };

    const removeProcessStep = (stepId: string) => {
      const steps = fmeaData.process_steps || [];
      const updatedSteps = steps.filter((step: any) => step.id !== stepId);
      updateFmeaData('process_steps', updatedSteps);
    };

    const addFailureMode = (stepId: string) => {
      const newFailureMode = {
        id: Date.now().toString(),
        failure_mode: '',
        potential_effects: '',
        potential_causes: '',
        current_controls: '',
        severity: 5,
        occurrence: 5,
        detection: 5,
        rpn: 125, // Severity × Occurrence × Detection
        recommended_actions: '',
        responsibility: '',
        target_completion: ''
      };
      
      const steps = fmeaData.process_steps || [];
      const updatedSteps = steps.map((step: any) => {
        if (step.id === stepId) {
          return {
            ...step,
            failure_modes: [...(step.failure_modes || []), newFailureMode]
          };
        }
        return step;
      });
      updateFmeaData('process_steps', updatedSteps);
    };

    const updateFailureMode = (stepId: string, failureModeId: string, field: string, value: string | number) => {
      const steps = fmeaData.process_steps || [];
      const updatedSteps = steps.map((step: any) => {
        if (step.id === stepId) {
          const updatedFailureModes = step.failure_modes.map((fm: any) => {
            if (fm.id === failureModeId) {
              const updated = { ...fm, [field]: value };
              // Calculate RPN when severity, occurrence, or detection changes
              if (['severity', 'occurrence', 'detection'].includes(field)) {
                updated.rpn = updated.severity * updated.occurrence * updated.detection;
              }
              return updated;
            }
            return fm;
          });
          return { ...step, failure_modes: updatedFailureModes };
        }
        return step;
      });
      updateFmeaData('process_steps', updatedSteps);
    };

    const removeFailureMode = (stepId: string, failureModeId: string) => {
      const steps = fmeaData.process_steps || [];
      const updatedSteps = steps.map((step: any) => {
        if (step.id === stepId) {
          return {
            ...step,
            failure_modes: step.failure_modes.filter((fm: any) => fm.id !== failureModeId)
          };
        }
        return step;
      });
      updateFmeaData('process_steps', updatedSteps);
    };

    const getRpnColor = (rpn: number) => {
      if (rpn >= 200) return 'text-red-600 bg-red-50';
      if (rpn >= 100) return 'text-orange-600 bg-orange-50';
      if (rpn >= 50) return 'text-yellow-600 bg-yellow-50';
      return 'text-green-600 bg-green-50';
    };

    const getRpnPriority = (rpn: number) => {
      if (rpn >= 200) return 'Critical';
      if (rpn >= 100) return 'High';
      if (rpn >= 50) return 'Medium';
      return 'Low';
    };

    return (
      <div className="space-y-8">
        {/* FMEA Header */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangleIcon className="h-8 w-8 text-yellow-600" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-900">FMEA Analysis</h3>
              <p className="text-yellow-700 text-sm">Failure Mode and Effects Analysis</p>
            </div>
          </div>
          <div className="bg-yellow-100 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>RPN (Risk Priority Number)</strong> = Severity × Occurrence × Detection
              <br />
              <span className="text-xs">Scale: 1-10 for each factor. Higher RPN indicates higher priority for action.</span>
            </p>
          </div>
        </div>

        {/* Process Steps */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-medium text-gray-900">Process Steps & Failure Modes</h4>
            {isEditing && (
              <button
                onClick={addProcessStep}
                className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Process Step</span>
              </button>
            )}
          </div>
          
          <div className="space-y-8">
            {(fmeaData.process_steps || []).map((step: any, stepIndex: number) => (
              <div key={step.id} className="border border-yellow-200 rounded-lg p-6 bg-yellow-25">
                {/* Process Step Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
                      Step {stepIndex + 1}
                    </span>
                    <h5 className="text-lg font-medium text-gray-900">
                      {step.process_step || 'Untitled Process Step'}
                    </h5>
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => removeProcessStep(step.id)}
                      className="text-red-600 hover:text-red-800 focus:outline-none"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Process Step Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Process Step</label>
                    <input
                      type="text"
                      value={step.process_step || ''}
                      onChange={(e) => updateProcessStep(step.id, 'process_step', e.target.value)}
                      placeholder="Describe the process step..."
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Function</label>
                    <input
                      type="text"
                      value={step.function || ''}
                      onChange={(e) => updateProcessStep(step.id, 'function', e.target.value)}
                      placeholder="What is the function of this step?"
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                        !isEditing ? 'bg-gray-50 text-gray-600' : ''
                      }`}
                    />
                  </div>
                </div>

                {/* Failure Modes */}
                <div className="border-t border-yellow-200 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h6 className="text-md font-medium text-gray-900">Failure Modes</h6>
                    {isEditing && (
                      <button
                        onClick={() => addFailureMode(step.id)}
                        className="flex items-center space-x-2 px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <PlusIcon className="h-3 w-3" />
                        <span>Add Failure Mode</span>
                      </button>
                    )}
                  </div>

                  <div className="space-y-6">
                    {(step.failure_modes || []).map((failureMode: any, fmIndex: number) => (
                      <div key={failureMode.id} className="bg-white border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded">
                              FM {fmIndex + 1}
                            </span>
                            <div className={`px-2 py-1 rounded text-xs font-medium ${getRpnColor(failureMode.rpn)}`}>
                              RPN: {failureMode.rpn} ({getRpnPriority(failureMode.rpn)})
                            </div>
                          </div>
                          {isEditing && (
                            <button
                              onClick={() => removeFailureMode(step.id, failureMode.id)}
                              className="text-red-600 hover:text-red-800 focus:outline-none"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        {/* Failure Mode Details */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Failure Mode</label>
                            <textarea
                              value={failureMode.failure_mode || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'failure_mode', e.target.value)}
                              placeholder="How could this step fail?"
                              disabled={!isEditing}
                              rows={2}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Potential Effects</label>
                            <textarea
                              value={failureMode.potential_effects || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'potential_effects', e.target.value)}
                              placeholder="What are the effects of this failure?"
                              disabled={!isEditing}
                              rows={2}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Potential Causes</label>
                            <textarea
                              value={failureMode.potential_causes || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'potential_causes', e.target.value)}
                              placeholder="What could cause this failure?"
                              disabled={!isEditing}
                              rows={2}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Current Controls</label>
                            <textarea
                              value={failureMode.current_controls || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'current_controls', e.target.value)}
                              placeholder="What controls are currently in place?"
                              disabled={!isEditing}
                              rows={2}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                        </div>

                        {/* Risk Assessment */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Severity (1-10)
                              <span className="text-gray-500 ml-1">Impact</span>
                            </label>
                            <select
                              value={failureMode.severity || 5}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'severity', parseInt(e.target.value))}
                              disabled={!isEditing}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            >
                              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Occurrence (1-10)
                              <span className="text-gray-500 ml-1">Frequency</span>
                            </label>
                            <select
                              value={failureMode.occurrence || 5}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'occurrence', parseInt(e.target.value))}
                              disabled={!isEditing}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            >
                              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Detection (1-10)
                              <span className="text-gray-500 ml-1">Detectability</span>
                            </label>
                            <select
                              value={failureMode.detection || 5}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'detection', parseInt(e.target.value))}
                              disabled={!isEditing}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            >
                              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">RPN</label>
                            <div className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${getRpnColor(failureMode.rpn)}`}>
                              {failureMode.rpn}
                            </div>
                          </div>
                        </div>

                        {/* Recommended Actions */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Recommended Actions</label>
                            <textarea
                              value={failureMode.recommended_actions || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'recommended_actions', e.target.value)}
                              placeholder="What actions should be taken?"
                              disabled={!isEditing}
                              rows={2}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Responsibility</label>
                            <input
                              type="text"
                              value={failureMode.responsibility || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'responsibility', e.target.value)}
                              placeholder="Who is responsible?"
                              disabled={!isEditing}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Target Completion</label>
                            <input
                              type="date"
                              value={failureMode.target_completion || ''}
                              onChange={(e) => updateFailureMode(step.id, failureMode.id, 'target_completion', e.target.value)}
                              disabled={!isEditing}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm ${
                                !isEditing ? 'bg-gray-50 text-gray-600' : ''
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {(!step.failure_modes || step.failure_modes.length === 0) && (
                      <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                        <AlertTriangleIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p>No failure modes identified yet</p>
                        {isEditing && (
                          <p className="text-sm">Click "Add Failure Mode" to start the analysis</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {(!fmeaData.process_steps || fmeaData.process_steps.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <AlertTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Process Steps Defined</h3>
                <p className="mb-4">Start by defining the process steps for your FMEA analysis</p>
                {isEditing && (
                  <p className="text-sm">Click "Add Process Step" to begin</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* FMEA Summary */}
        {fmeaData.process_steps && fmeaData.process_steps.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">FMEA Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {fmeaData.process_steps.reduce((count: number, step: any) => 
                    count + (step.failure_modes || []).filter((fm: any) => fm.rpn >= 200).length, 0
                  )}
                </div>
                <div className="text-sm text-red-800">Critical (RPN ≥200)</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">
                  {fmeaData.process_steps.reduce((count: number, step: any) => 
                    count + (step.failure_modes || []).filter((fm: any) => fm.rpn >= 100 && fm.rpn < 200).length, 0
                  )}
                </div>
                <div className="text-sm text-orange-800">High (RPN 100-199)</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {fmeaData.process_steps.reduce((count: number, step: any) => 
                    count + (step.failure_modes || []).filter((fm: any) => fm.rpn >= 50 && fm.rpn < 100).length, 0
                  )}
                </div>
                <div className="text-sm text-yellow-800">Medium (RPN 50-99)</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {fmeaData.process_steps.reduce((count: number, step: any) => 
                    count + (step.failure_modes || []).filter((fm: any) => fm.rpn < 50).length, 0
                  )}
                </div>
                <div className="text-sm text-green-800">Low (RPN &lt;50)</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFilesContent = () => {
    const handleFileUpload = async (files: FileList) => {
      if (!files.length) return;
      
      setUploadingFiles(true);
      
      try {
        const uploadPromises = Array.from(files).map(file => 
          fileAttachmentApi.create({
            kata_board: kataBoard.id,
            attachment_type: 'observation_file',
            title: file.name,
            description: `Uploaded for process observations`,
            file: file
          })
        );
        
        const uploadedFiles = await Promise.all(uploadPromises);
        setAttachments(prev => [...(Array.isArray(prev) ? prev : []), ...uploadedFiles]);
      } catch (error) {
        console.error('Failed to upload files:', error);
      } finally {
        setUploadingFiles(false);
      }
    };

    const handleDeleteFile = async (fileId: number) => {
      if (!fileId) {
        console.error('Cannot delete file: invalid ID');
        return;
      }
      
      confirmDelete('this file', async () => {
        try {
          await fileAttachmentApi.delete(fileId);
          setAttachments(prev => (Array.isArray(prev) ? prev : []).filter(file => file.id !== fileId));
        } catch (error) {
          console.error('Failed to delete file:', error);
        }
      });
    };

    const getFileIcon = (file: FileAttachment) => {
      if (file.is_image) return '🖼️';
      if (file.is_pdf) return '📄';
      return '📎';
    };

    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
      <div className="space-y-8">
        {/* Files Header */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
          <div className="flex items-center space-x-3 mb-4">
            <FileIcon className="h-8 w-8 text-purple-600" />
            <div>
              <h3 className="text-lg font-semibold text-purple-900">File Attachments</h3>
              <p className="text-purple-700 text-sm">Documents and supporting files for process observations</p>
            </div>
          </div>
        </div>

        {/* File Upload Area */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
              <div className="space-y-1 text-center">
                <FileIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
                  >
                    <span>Upload files</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      multiple
                      onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, PDF, DOC, DOCX up to 10MB
                </p>
              </div>
            </div>
          </div>

          {uploadingFiles && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm text-blue-700">Uploading files...</span>
              </div>
            </div>
          )}
        </div>

        {/* Files List */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Attached Files</h4>
          
          {(Array.isArray(attachments) ? attachments : []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(Array.isArray(attachments) ? attachments : []).map((file, index) => (
                <div key={file.id || `file-${index}`} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getFileIcon(file)}</span>
                      <div className="min-w-0 flex-1">
                        <h5 className="text-sm font-medium text-gray-900 truncate">
                          {file.title || file.original_filename}
                        </h5>
                        <p className="text-xs text-gray-500 truncate">
                          {file.original_filename}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteFile(file.id)}
                      className="text-red-600 hover:text-red-800 focus:outline-none"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="text-xs text-gray-500 mb-3">
                    <p>Size: {formatFileSize(file.file_size || 0)}</p>
                    <p>Uploaded: {new Date(file.created_at).toLocaleDateString()}</p>
                  </div>

                  {file.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {file.description}
                    </p>
                  )}

                  <div className="flex space-x-2">
                    {fileAttachmentApi.canPreview(file) && (
                      <button
                        onClick={() => setPreviewFile(file)}
                        className="flex-1 px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        Preview
                      </button>
                    )}
                    <a
                      href={fileAttachmentApi.getDownloadUrl(file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                        fileAttachmentApi.canPreview(file) ? 'flex-none' : 'flex-1 text-center'
                      }`}
                    >
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No files attached yet</h3>
              <p className="mb-4">Upload files to support your process observations</p>
            </div>
          )}
        </div>

        {/* File Categories Summary */}
        {(Array.isArray(attachments) ? attachments : []).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">File Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {(Array.isArray(attachments) ? attachments : []).filter(file => file.is_image).length}
                </div>
                <div className="text-sm text-blue-800">Images</div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {(Array.isArray(attachments) ? attachments : []).filter(file => file.is_pdf).length}
                </div>
                <div className="text-sm text-red-800">PDFs</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">
                  {(Array.isArray(attachments) ? attachments : []).filter(file => !file.is_image && !file.is_pdf).length}
                </div>
                <div className="text-sm text-gray-800">Other Files</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderObservationContent = () => {
    // Content for different observation types
    switch (currentTab) {
      case 'fourD':
        return renderFourDContent();
      case 'ids':
        return renderIdsContent();
      case 'fmea':
        return renderFmeaContent();
      case 'files':
        return renderFilesContent();
      default:
        return <div>Unknown observation type</div>;
    }
  };

  // Show loading state
  if (loading && observations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900">Loading Process Observations</h3>
          <p className="mt-1 text-sm text-gray-500">Please wait...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-600 mb-4">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">Error Loading Process Observations</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Tab Navigation - Always show in both edit and display modes */}
        <div className="border-b border-gray-200 bg-white">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const isActive = currentTab === tab.id;
              const observationCount = Array.isArray(observations) 
                ? observations.filter(obs => obs.observation_type === tab.id).length
                : 0;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setCurrentTab(tab.id);
                    setSelectedObservation(null);
                  }}
                  className={`navigation-btn flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? `border-${tab.color}-500 text-${tab.color}-600`
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                  {observationCount > 0 && (
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      isActive 
                        ? `bg-${tab.color}-100 text-${tab.color}-800`
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {observationCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {currentTab !== 'files' && renderObservationList()}
          {renderMainContent()}
        </div>
      </div>
      
      {/* Alert Component */}
      {AlertComponent}
      
      {/* File Preview Modal */}
      {previewFile && (
        <ResizablePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </>
  );
};

export default Component05_ProcessObservations;

